import { Center, Loader } from '@hover/blueprint';
import React, { useEffect, useMemo } from 'react';
import { Redirect } from 'react-router';
import { useParams } from 'react-router-dom';

import { SharePageLayout } from 'src/modules/homeownerApp/components/SharePageLayout';
import {
  useFilteredOutUneditedOriginalImages,
  useHomeownerAppContext,
} from 'src/modules/homeownerApp/hooks';
import { FeatureTracking } from 'src/shared/constants/FeatureFlag';
import type { AiDisplayImage } from 'src/shared/graphql/generated';
import { useGetProjectShareQuery } from 'src/shared/graphql/generated';
import { useEffectOnMount } from 'src/shared/hooks';
import { Route, Route as Routes } from 'src/shared/route';
import { pageViewed } from 'src/shared/telemetry';
import { logError } from 'src/shared/telemetry/logger';

const ProjectShare = () => {
  const { id } = useParams<{ id: string }>();

  const context = useHomeownerAppContext();
  const { trackingData } = context;

  const { data, error, loading } = useGetProjectShareQuery({
    variables: { id },
  });

  useEffect(() => {
    if (error) logError(error);
  }, [error]);

  const { aiDisplayImages, orgId, shareCtaPath, creatingUser } =
    data?.projectShare || {};
  const images: AiDisplayImage[] = useFilteredOutUneditedOriginalImages(
    (aiDisplayImages ?? []) as AiDisplayImage[],
  ) as AiDisplayImage[];

  useEffectOnMount(() => {
    pageViewed({
      ...trackingData,
      feature: 'lead-landing-page',
      page_or_screen_name: 'lead-landing-page',
      track_id: 'lead-landing-page-view',
      org_id: orgId ?? undefined,
    });
  });

  const newTrackingData = useMemo(
    () => ({
      orgId,
      feature: FeatureTracking.LEAD_LANDING_PAGE,
      page_or_screen_name: FeatureTracking.LEAD_LANDING_PAGE,
    }),
    [orgId],
  );

  const contactUser = {
    name: creatingUser?.name ?? undefined,
    email: creatingUser?.email ?? undefined,
    phone: creatingUser?.phone ?? undefined,
  };

  const getShouldRedirectShare =
    (!loading && data?.projectShare === null) || // deleted project or not in split treatment
    (!loading && data?.projectShare?.aiDisplayImages?.length === 0); // all associated images deleted

  if (getShouldRedirectShare) {
    return (
      <Redirect
        from={Routes.PROJECT_SHARE.path}
        to={Routes.HOMEOWNER_APP_HOME.path}
      />
    );
  }

  // TODO: handle error case
  if (loading || !aiDisplayImages) {
    return (
      <Center bottom="0" left="0" position="fixed" right="0" top="0">
        <Loader color="neutral.900" size="huge" />
      </Center>
    );
  }

  return (
    <SharePageLayout
      details={{
        ctaPath: shareCtaPath ?? '',
        contactUser,
      }}
      fullscreenImagePath={Route.PROJECT_SHARE_FULLSCREEN.path}
      fullscreenReturnPath={Route.PROJECT_SHARE.path}
      images={images}
      projectOrgId={orgId ?? undefined}
      shareId={id}
      trackingData={newTrackingData}
    />
  );
};

export { ProjectShare };
